import { useEffect, useMemo, useRef, useState } from "react"
import { ListData_ValueTypes, ListStyle_Types, NewFieldTypes, TableStyles_Types } from "../../Definitions"
import { colors, Units } from '../../Styles';
import { NewModal } from '../../Components'
import { useEditableColumnConstructor } from "../Tables/_Tables/useEditableColumnConstructor";
import { getCoreRowModel, noop, useReactTable } from "@tanstack/react-table";
import { ReactiveTable } from "../Tables/reactiveTableRenderer";

import { GenericDataParameters} from "../Tables/_Tables/useColumnConstructor";
import { ButtonList, MainContentPaddingTypes } from "../BaseComponents";
import { FeedCurve, FeedCurveTime } from "../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedCurvesCall";
import { useAppSelector } from "../../Store";
import { Graph_QL } from "../../Services";
import { useFeedCurves } from "../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves";
import { convertFeedCurveToSpreadingData, convertMinutesToTime, divStyleOverride } from "./_FeedCurves/feedCurveSpreadingUtil";
import { MaintainFeedCurve } from "../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/maintainFeedCurveCall";
import { valueIsNumberValue } from "./_FeedCurves/feedCurveTableUtils";
import { cloneDeep, keyBy, merge, partition } from "lodash";
import { useTranslation } from "react-i18next";
import NewTimePicker, { CommitBtnStyle } from "../BaseComponents/newTimePicker";

interface feedCurveSpreadingProps {
  isModalOpen: boolean
  onClose: () => void
  feedCurve: FeedCurve
}

export default function FeedCurveSpreading({ feedCurve, isModalOpen, onClose }: feedCurveSpreadingProps) {
  const {t} = useTranslation()
  const farm = useAppSelector(({activeData}) => activeData.farm)
  const connection = useMemo(() => (Graph_QL.create({domain: farm ?? ""})),[farm]);
 
  const {maintainEntries: maintainCurveSpreading } = useFeedCurves(connection);
  

  const [feedTimes, setFeedTimes] = useState<Partial<FeedCurveTime>[]>(cloneDeep(feedCurve.times)?.sort((feedCurveTimeA, feedCurveTimeB) => (feedCurveTimeA.time - feedCurveTimeB.time)) ?? []);
  const [feedPlans, setFeedPlans] = useState(() => {
    if (!feedCurve.milestones) {
      return []
    };
    return cloneDeep(feedCurve.milestones)
      .sort((milestoneA, milestoneB) => (milestoneA.milestoneIndex - milestoneB.milestoneIndex))
      .map((milestone) => {
        if (milestone.feedPlan) {milestone.feedPlan.plan.push({day: 0, timeId: 0, amount: 0, feedPlanEntryId: 0})}
        return milestone.feedPlan?.plan ? [...milestone.feedPlan.plan].sort((entryA, entryB) => entryA.day - entryB.day) : [{day: 0, timeId: 0, amount: 0, feedPlanEntryId: 0}] 
      })
  })

  const clearDirt = () => {
    dirtRef.current = {feedCurveId: feedCurve.feedCurveId}
    setFeedTimes(feedCurve.times ?? []);
    setFeedPlans(() => {
    if (!feedCurve.milestones) {
      return []
    };
    return cloneDeep(feedCurve.milestones)
      .sort((milestoneA, milestoneB) => (milestoneA.milestoneIndex - milestoneB.milestoneIndex))
      .map((milestone) => {
        if (milestone.feedPlan) {milestone.feedPlan.plan.push({day: 0, timeId: 0, amount: 0, feedPlanEntryId: 0})}
        return milestone.feedPlan?.plan ? milestone.feedPlan.plan : [{day: 0, timeId: 0, amount: 0, feedPlanEntryId: 0}] 
      })
  })
  }

  useEffect(() => {
    setFeedTimes(feedCurve.times ?? []);
    setFeedPlans(() => {
    if (!feedCurve.milestones) {
      return []
    };
    return cloneDeep(feedCurve.milestones)
      .sort((milestoneA, milestoneB) => (milestoneA.milestoneIndex - milestoneB.milestoneIndex))
      .map((milestone) => {
        if (milestone.feedPlan) {milestone.feedPlan.plan.push({day: 0, timeId: 0, amount: 0, feedPlanEntryId: 0})}
        return milestone.feedPlan?.plan ? [...milestone.feedPlan.plan].sort((entryA, entryB) => entryA.day - entryB.day) : [{day: 0, timeId: 0, amount: 0, feedPlanEntryId: 0}] 
      })
  })
  },[feedCurve])

  const dirtRef = useRef<MaintainFeedCurve>({feedCurveId: feedCurve.feedCurveId})
  const data = useMemo(() => (convertFeedCurveToSpreadingData(feedCurve, feedPlans, setFeedPlans)),[feedCurve, feedPlans])
  
  const tableData = useMemo(() => {
    return Array.from(data.values());
  }, [data]);

  const accessorData = useMemo<GenericDataParameters[]>(() => {
    return [
      // { headerValue: t("feedCurvePages.feedCurve.spreading", {defaultValue: "spreading"}), accessor: "milestoneSpreading", type: ListData_ValueTypes.Check, width: "120px" },
      { headerValue: t("milestones.general.milestone", {defaultValue: "milestone"}), accessor: "milestoneName", type: ListData_ValueTypes.Display, staticColumn: true },
      { headerValue: t("feedCurvePages.feedCurve.day", {defaultValue: "day"}), accessor: "id", type: ListData_ValueTypes.String, width: "80px"},
      { headerValue: "", accessor: "addDay", type: ListData_ValueTypes.Button, width: "42px"},
      { headerValue: "", accessor: "removeRowActionColumn", type: ListData_ValueTypes.DeleteRowAction, width: "41px"},
      ...feedTimes.map((feedTime) => ({
        headerValue: convertMinutesToTime(feedTime.time ?? 0),
        accessor: feedTime.timeId?.toString() ?? "",
        type: ListData_ValueTypes.Number,
        unit: Units.pct_white
        // staticColumn: tableData.some(row => row.milestoneSpreading === false) ? false : true
      })),
      { headerValue: t("units.total", {defaultValue: "total"}), accessor: "total", type: ListData_ValueTypes.Display, staticColumn: true, unit: Units.pct_white, divStyleOverride: divStyleOverride},
    ];
  }, [feedTimes, t]);
  
  const columns = useEditableColumnConstructor({
    accessorData: [...accessorData],
    tableStyle: TableStyles_Types.blueTable,
    canEdit: true,
  });
  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnPinning: {
        left: ["milestoneSpreading", "milestoneName", "id", "addDay", "removeRowActionColumn"],
        right: ["total"]
      },
      columnOrder: ["milestoneSpreading", "milestoneName", "id", "addDay", "removeRowActionColumn", ...feedTimes.map((time) => (time.timeId?.toString() ?? "")), "nonValidAccessorValue", "total"]
    },
    meta: {
      updateData: (rowId, columnId, value) => {
        const oldRow = data.get(rowId);
        if (feedCurve.milestones) { 
          if (columnId === "milestoneSpreading") {
            return
          } 
          if (columnId === "id" && valueIsNumberValue(value, columnId) && oldRow) {
            const feedPlanEntries = feedPlans[oldRow.milestoneArrayIndex].filter((feedPlanEntry) => (feedPlanEntry.day === oldRow.id-oldRow.milestoneFirstDay));
            feedPlanEntries.forEach((feedPlanEntry) => {
              feedPlanEntry.day = value-oldRow.milestoneFirstDay;
            })
            
            const [newValue, updateValue] = partition(feedPlanEntries, (entry) => (oldRow.newDay || entry.feedPlanEntryId < 1));

            if (newValue.length > 0) {
              if (dirtRef.current.creates === undefined) {
                dirtRef.current.creates = {};
              }

              const newFeedPlanEntries = newValue.map(({amount, day, timeId}) => ({ amount: +amount, day, time_id: timeId, temporaryId: `${day}-${timeId}`}))

              // Since a valid feedCurveMilestoneId is supplied the 
              const newMilestoneEntry = {feedCurveMilestoneId: oldRow.feedCurveMilestoneId, temporaryId: oldRow.feedCurveMilestoneId, milestone_index: oldRow.milestoneIndex, milestone_id: oldRow.milestoneId, feedPlan: newFeedPlanEntries }
              if (dirtRef.current.creates.milestones) {
                const dirtyMilestone = dirtRef.current.creates.milestones.find((milestone) => (milestone.temporaryId === oldRow.feedCurveMilestoneId));
                if (dirtyMilestone) {
                  const oldTemporaryIds = newValue.map((entry) => (`${oldRow.id}-${entry.timeId}`));
                  dirtyMilestone.feedPlan = Object.values(merge({}, keyBy(dirtyMilestone.feedPlan, "temporaryId"), keyBy(newFeedPlanEntries, "temporaryId"))).filter((dirtyEntry) => (!oldTemporaryIds.includes(dirtyEntry.temporaryId)));
                } else {
                  dirtRef.current.creates.milestones = [...dirtRef.current.creates.milestones, newMilestoneEntry]
                }
              } else {
                dirtRef.current.creates.milestones = [newMilestoneEntry];
              }
            }

            if (updateValue.length > 0) {
              if (dirtRef.current.updates === undefined) {
                dirtRef.current.updates = {};
              }
              dirtRef.current.updates.feedPlan = Object.values(merge({}, keyBy(dirtRef.current.updates.feedPlan, "feedPlanEntryId"), keyBy(updateValue.map((entry) => ({feedPlanEntryId: entry.feedPlanEntryId, baseInfo: {day: entry.day}})), "feedPlanEntryId")))
            }
            setFeedPlans(cloneDeep(feedPlans));
            return
            
          }
          if (oldRow && valueIsNumberValue(value, columnId)) {
            const feedPlanEntry = feedPlans[oldRow.milestoneArrayIndex].find((feedPlan) => (feedPlan.day === rowId-oldRow.milestoneFirstDay && feedPlan.timeId === +columnId));
            if (+columnId <0 || oldRow.newDay || feedPlanEntry === undefined || feedPlanEntry.feedPlanEntryId < 1) {
              // We are in a new column or a new row day or the value for the day haven't existed or we need to update a create value
              if (dirtRef.current.creates === undefined) {
                dirtRef.current.creates = {};
              }
              feedPlanEntry ? feedPlanEntry.amount = +value : feedPlans[oldRow.milestoneArrayIndex].push({day: oldRow.id-oldRow.milestoneFirstDay, amount: +value, feedPlanEntryId: 0, timeId: +columnId})
              
              const newFeedPlanEntry = {amount: +value, day: rowId, time_id: +columnId, temporaryId: `${rowId}-${columnId}`}
              const newMilestoneEntry = {feedCurveMilestoneId: oldRow.feedCurveMilestoneId, temporaryId: oldRow.feedCurveMilestoneId, milestone_index: oldRow.milestoneIndex, milestone_id: oldRow.milestoneId, feedPlan: [newFeedPlanEntry]}
              
              if (dirtRef.current.creates.milestones) {
                const dirtyMilestone = dirtRef.current.creates.milestones.find((milestone) => (milestone.temporaryId === oldRow.feedCurveMilestoneId));
                if (dirtyMilestone) {
                  dirtyMilestone.feedPlan = Object.values(merge({}, keyBy(dirtyMilestone.feedPlan, "temporaryId"), keyBy([newFeedPlanEntry], "temporaryId"))) 
                  setFeedPlans(cloneDeep(feedPlans));
                  return
                }
              }

              dirtRef.current.creates.milestones = Object.values(merge({}, keyBy(dirtRef.current.creates.milestones, "temporaryId"), keyBy([newMilestoneEntry], "temporaryId")));
              setFeedPlans(cloneDeep(feedPlans));
              return
            }
            
            if (dirtRef.current.updates === undefined) {
              dirtRef.current.updates = {};
            }
            if (dirtRef.current.updates.feedPlan === undefined) {
              dirtRef.current.updates.feedPlan = [];
            }
            feedPlanEntry.amount = +value;
            dirtRef.current.updates.feedPlan = Object.values(merge({}, keyBy(dirtRef.current.updates.feedPlan, "feedPlanEntryId"), keyBy([{feedPlanEntryId: feedPlanEntry.feedPlanEntryId, baseInfo: {amount: +value}}], "feedPlanEntryId")))
            setFeedPlans(cloneDeep(feedPlans))
            return
          }
          if (oldRow) {
            const feedPlanEntryIndex = feedPlans[oldRow.milestoneArrayIndex].findIndex((feedPlan) => (feedPlan.day === rowId-oldRow.milestoneFirstDay && feedPlan.timeId === +columnId))
            if (feedPlanEntryIndex === undefined) {
              return;
            }
            const feedPlanEntry = feedPlans[oldRow.milestoneArrayIndex][feedPlanEntryIndex]
 
            if (+columnId < 0 || oldRow.newDay || feedPlanEntry.feedPlanEntryId < 1) {
              const dirtyMilestoneIndex = dirtRef.current.creates?.milestones?.findIndex((milestone) => (milestone.temporaryId === oldRow.milestoneId));
              if (dirtRef.current.creates?.milestones && dirtyMilestoneIndex) {
                dirtRef.current.creates.milestones[dirtyMilestoneIndex].feedPlan = dirtRef.current.creates.milestones[dirtyMilestoneIndex].feedPlan?.filter((feedPlan) => !(feedPlan.temporaryId === `${rowId}-${columnId}`)) 
              }
              feedPlans[oldRow.milestoneArrayIndex].splice(feedPlanEntryIndex, 0)
              setFeedPlans(cloneDeep(feedPlans));
              return
            }
            if (dirtRef.current.deletes === undefined) {
              dirtRef.current.deletes = {};
            }
            if (dirtRef.current.deletes.feedPlan === undefined) {
              dirtRef.current.deletes.feedPlan = [];
            }
            dirtRef.current.deletes.feedPlan.push(feedPlanEntry.feedPlanEntryId);
            feedPlans[oldRow.milestoneArrayIndex].splice(feedPlanEntryIndex, 1)
            setFeedPlans(cloneDeep(feedPlans));
            return 
          }
        }

      },
      deleteRow: (deletedRowId) => noop(),
      allowDeleteRowCheck: (row) => {return (!row.original.milestoneName)}
    }
  });
  const reactiveTable = ReactiveTable({
    table,
    tableType: TableStyles_Types.blueTable,
    emptyText: t("feed.feedCurve.noFeedCurve", {defaultValue: "no feedCurve found"}),
    renderFooter: true,
  });
  if (!feedCurve) return null;
  return ( <>
    <NewModal
      header={t("feedCurvePages.feedCurve.spreading", {defaultValue: "spreading"})}
      modalState={isModalOpen}
      predefinedButtons={{
        onClose: () => {clearDirt(); onClose()}
      }}
      headerContent={
        <div style={{display: "flex", alignItems: "end", justifyContent: "space-between", width: "100%", padding: "0px 20px"}}>
          <NewTimePicker 
            header={t("feedCurvePages.feedCurve.addTime", {defaultValue: "add time"})}
            commitBtn
            background
            overrides={{
              fieldType: NewFieldTypes.tertiaryField,
              commitBtnStyle: CommitBtnStyle.dark,
              backgroundColor: colors.blueDarkSecondary
            }}
            onTimeSelect={
              (date) => {
                if (dirtRef.current.creates === undefined) {
                  dirtRef.current.creates = {}
                }
                const newTime = date.getHours()*60 + date.getMinutes();
                dirtRef.current.creates.times = Object.values(merge({}, keyBy(dirtRef.current.creates.times, "time"), keyBy([{time: newTime, time_id: -newTime }])));
                setFeedTimes((oldFeedTimes) => {
                  const copy = [...oldFeedTimes]
                  if (!oldFeedTimes.map((feedTime) => feedTime?.time).includes(newTime)) {
                    copy.push({timeId: -newTime, time: newTime})
                  }
                  return copy.sort((timeA, timeB) => (timeA.time ?? 1) - (timeB.time ?? 0));
                })
              }
            }
          />
          
        </div>
      }
      mainContent={<>
        <div style={{ marginTop: "30px" }}>{reactiveTable}</div>
        {/* <div style={{width: "100%", display: "flex", justifyContent: "center",}}>
          <ButtonList 
            contents={[t("feedCurvePages.feedCurve.saveChanges", {defaultValue: "save changes"}), t("feedCurvePages.feedCurve.resetChanges", {defaultValue: "reset changes"})]}
            onClick={(i) => {
              if (i === 0) {
                if (maintainCurveSpreading) {
                  maintainCurveSpreading({data: dirtRef.current.updates}, {data:dirtRef.current.deletes}, {data: dirtRef.current.creates}, {originalFeedCurve: feedCurve, optimisticFeedCurve: feedCurve}) // TODO optimistic update
                }
                clearDirt();
                onClose();
              }
              if (i === 1) {clearDirt()}
            }}
            overrides={{
              baseButton: { width:"fit-content"},
              // contentStyles: {textShadow: "none"}
            }}
          />
        </div> */}
      </>}
      footerContent={
        <ButtonList 
          contents={[t("feedCurvePages.feedCurve.saveChanges", {defaultValue: "save changes"}), t("feedCurvePages.feedCurve.resetChanges", {defaultValue: "reset changes"})]}
          onClick={(i) => {
            if (i === 0) {
              if (maintainCurveSpreading) {
                maintainCurveSpreading({data: dirtRef.current.updates}, {data:dirtRef.current.deletes}, {data: dirtRef.current.creates}, {originalFeedCurve: feedCurve, optimisticFeedCurve: feedCurve}) // TODO optimistic update
              }
              clearDirt();
              onClose();
            }
            if (i === 1) {clearDirt()}
          }}
          overrides={{
            baseButton: {width: "fit-content"}
          }}
        />
      }
    />
  </>);
}
 